import type { RoleName } from "@storytell/types";
import type { TranslationKey } from "~/i18n/translations";

export const ASSIGNABLE_ORG_ROLES: readonly RoleName[] = ["role.owner", "role.collaborator", "role.reader"];

/** Least to most capable; every role picker takes this order so no two run opposite ways. */
export const ORG_ROLES_BY_CAPABILITY: readonly RoleName[] = ASSIGNABLE_ORG_ROLES.toReversed();

/**
 * The roles a project grant could raise `role` to. Grants are elevate-only: one
 * ranked at or below the member's organization-wide role writes a live row that
 * changes nothing they can do, so offering it is offering a dead option. The top
 * role returns nothing, since no grant can raise it and none can narrow it.
 */
export const rolesOutranking = (role: RoleName | null): readonly RoleName[] =>
  role === null ? ORG_ROLES_BY_CAPABILITY : ORG_ROLES_BY_CAPABILITY.slice(ORG_ROLES_BY_CAPABILITY.indexOf(role) + 1);

/**
 * What a grant's role select lists. A redundant grant holds a role no raise
 * would keep, and the select still has to show the role the grant actually
 * carries, so that one leads the list — rendered unselectable, since the
 * backend rejects re-applying it.
 */
export const grantRoleOptions = (projectRole: RoleName, raisable: readonly RoleName[]): readonly RoleName[] =>
  raisable.includes(projectRole) ? raisable : [projectRole, ...raisable];

const ROLE_LABELS: Record<RoleName, TranslationKey> = {
  "role.owner": "organization.members.role_owner",
  "role.collaborator": "organization.members.role_collaborator",
  "role.reader": "organization.members.role_reader",
};

export const roleLabelKey = (role: RoleName): TranslationKey => ROLE_LABELS[role];

const ROLE_CONSEQUENCES: Record<RoleName, TranslationKey> = {
  "role.owner": "organization.roles.consequence_owner",
  "role.collaborator": "organization.roles.consequence_collaborator",
  "role.reader": "organization.roles.consequence_reader",
};

export const roleConsequenceKey = (role: RoleName): TranslationKey => ROLE_CONSEQUENCES[role];
